import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { MedienVorschau } from './MedienVorschau';

/**
 * **Was vom Gerät kommt: in welcher Reihenfolge, und wohin** (Sascha,
 * 13. Oktober: „Kann man bei der Auswahl der Medien gleich die einfüge
 * Reihenfolge festlegen? Manchmal geht das doch. Und auch gleich die Option,
 * direkt einzufügen“).
 *
 * Die Fotomediathek des iPhones gibt die Dateien nicht verlässlich in der
 * Reihenfolge des Antippens heraus -- manchmal ja, manchmal nach Datum. Also
 * steht sie hier noch einmal, nummeriert, bevor etwas hochgeladen wird: so,
 * wie sie hier steht, kommt sie in den Film. Umgestellt wird mit ↑ und ↓ --
 * Ziehen in einer Liste ist am Telefon ein Glücksspiel mit dem Rollen.
 */
export function GeraeteAuswahl({
  dateien,
  kopf,
  mitFilm,
  spaeterWohin = false,
  onLos,
}: {
  dateien: readonly File[];
  /** Ab wo im Film, in Worten -- „00:12“. */
  kopf: string;
  /** Ob „direkt in den Film“ überhaupt geht (beim Ersetzen nicht). */
  mitFilm: boolean;
  /** Ohne Stelle (über „Hinzufügen“): die Stelle wird danach im Schnitt angetippt. */
  spaeterWohin?: boolean;
  onLos: (reihe: File[], ziel: 'film' | 'medien') => void;
}): ReactElement {
  const [reihe, setReihe] = useState<File[]>(() => [...dateien]);
  const [ziel, setZiel] = useState<'film' | 'medien'>(mitFilm ? 'film' : 'medien');
  /** Welche Datei gerade über den ganzen Schirm läuft (16. Oktober). */
  const [ansehen, setAnsehen] = useState<File | null>(null);
  /**
   * **Reihenfolge antippen** wie in der Fotomediathek (Sascha, 16. Oktober:
   * „beim Auswählen der Medien auf dem Gerät gleich die Reihenfolge
   * beachten. Das können die Geräte doch“). Die Nummern, die das iPhone
   * beim Antippen zeigt, gibt Safari der Seite nicht mit -- also werden sie
   * hier genauso vergeben: antippen in der gewünschten Reihenfolge, fertig.
   */
  const [getippt, setGetippt] = useState<File[] | null>(null);
  const tippe = (datei: File) => {
    if (!getippt) return;
    // Noch einmal angetippt nimmt die Nummer zurück.
    const neu = getippt.includes(datei) ? getippt.filter((d) => d !== datei) : [...getippt, datei];
    // Alle angetippt: das ist die Reihenfolge.
    if (neu.length === reihe.length) {
      setReihe(neu);
      setGetippt(null);
    } else setGetippt(neu);
  };
  const tippenFertig = () => {
    if (getippt) setReihe([...getippt, ...reihe.filter((d) => !getippt.includes(d))]);
    setGetippt(null);
  };

  // Kleine Vorschaubilder aus der Datei selbst -- es ist noch nichts hochgeladen.
  const adressen = useMemo(() => new Map(dateien.map((d) => [d, URL.createObjectURL(d)])), [dateien]);
  useEffect(() => () => adressen.forEach((a) => URL.revokeObjectURL(a)), [adressen]);

  const tausche = (i: number, j: number) =>
    setReihe((r) => {
      if (j < 0 || j >= r.length) return r;
      const neu = [...r];
      [neu[i], neu[j]] = [neu[j]!, neu[i]!];
      return neu;
    });

  return (
    <div data-auswahl>
      {reihe.length > 1 ? (
        <p className="hint" style={{ marginTop: 0 }}>
          So, wie sie hier stehen, kommen sie in den Film. Umstellen mit ↑ und ↓ -- oder „Reihenfolge antippen“. Ein
          Tipp aufs Bild zeigt es ganz.
        </p>
      ) : null}
      <div className="list" data-auswahl-reihe>
        {reihe.map((datei, i) => {
          const art = datei.type.startsWith('image/') ? 'Bild' : datei.type.startsWith('audio/') ? 'Ton' : 'Video';
          const adresse = adressen.get(datei);
          const nummer = getippt ? getippt.indexOf(datei) + 1 : i + 1;
          return (
            <div
              className={`row${getippt ? ' auswahl-tippen' : ''}${getippt && nummer > 0 ? ' getippt' : ''}`}
              key={adresse ?? datei.name}
              data-auswahl-datei={datei.name}
              onClick={getippt ? () => tippe(datei) : undefined}
            >
              <span className="badge" style={{ minWidth: 22, textAlign: 'center' }} data-auswahl-nummer>
                {nummer > 0 ? nummer : '·'}
              </span>
              <button
                className="thumb-knopf"
                aria-label={`${datei.name} ansehen`}
                data-auswahl-ansehen
                onClick={(e) => {
                  e.stopPropagation();
                  setAnsehen(datei);
                }}
                style={{ width: 44, height: 44, flex: '0 0 44px', borderRadius: 8, overflow: 'hidden', background: 'var(--surface-2)' }}
              >
                {adresse && art === 'Bild' ? (
                  <img src={adresse} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : adresse && art === 'Video' ? (
                  <video src={`${adresse}#t=0.1`} muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : art === 'Ton' ? (
                  <span style={{ display: 'grid', placeItems: 'center', height: '100%', fontSize: 18 }}>♪</span>
                ) : null}
              </button>
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {datei.name}
                </div>
                <div className="sub">{art}</div>
              </div>
              {reihe.length > 1 && !getippt ? (
                <>
                  <button className="small" aria-label={`${datei.name} nach oben`} data-auswahl-hoch disabled={i === 0} onClick={() => tausche(i, i - 1)}>
                    ↑
                  </button>
                  <button
                    className="small"
                    aria-label={`${datei.name} nach unten`}
                    data-auswahl-runter
                    disabled={i === reihe.length - 1}
                    onClick={() => tausche(i, i + 1)}
                  >
                    ↓
                  </button>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
      {reihe.length > 1 ? (
        getippt ? (
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <span className="hint" style={{ margin: 0, flex: 1 }}>
              In der gewünschten Reihenfolge antippen ({getippt.length} von {reihe.length}).
            </span>
            <button className="small" onClick={tippenFertig} data-auswahl-antippen-fertig>
              Fertig
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button className="small" onClick={() => setGetippt([])} data-auswahl-antippen>
              Reihenfolge antippen
            </button>
            <button
              className="small"
              data-auswahl-zeit
          onClick={() => setReihe((r) => [...r].sort((a, b) => a.lastModified - b.lastModified))}
        >
              Nach Aufnahmezeit ordnen
            </button>
          </div>
        )
      ) : null}
      {mitFilm ? (
        <div className="chips" style={{ marginTop: 14 }} data-auswahl-ziele>
          <button className={`chip${ziel === 'film' ? ' active' : ''}`} data-auswahl-ziel="film" onClick={() => setZiel('film')}>
            {spaeterWohin ? 'In den Film -- Stelle danach antippen' : `Direkt in den Film, ab ${kopf}`}
          </button>
          <button className={`chip${ziel === 'medien' ? ' active' : ''}`} data-auswahl-ziel="medien" onClick={() => setZiel('medien')}>
            Nur zu den Medien
          </button>
        </div>
      ) : null}
      {ansehen && adressen.get(ansehen) ? (
        <MedienVorschau
          name={ansehen.name}
          art={ansehen.type.startsWith('image/') ? 'image' : ansehen.type.startsWith('audio/') ? 'audio' : 'video'}
          src={adressen.get(ansehen)!}
          onClose={() => setAnsehen(null)}
        />
      ) : null}
      <button
        className="primary"
        style={{ width: '100%', marginTop: 14 }}
        data-auswahl-los
        disabled={getippt !== null}
        onClick={() => onLos(reihe, ziel)}
      >
        {ziel === 'film' && spaeterWohin
          ? 'Weiter -- Stelle wählen'
          : reihe.length === 1
            ? 'Hochladen'
            : `${reihe.length} Dateien hochladen`}
      </button>
    </div>
  );
}
